/**
 * Round 96d (Jul 2026) — Payment Received pop-up modal.
 *
 * Mirrors the ExDivAlertModal pattern but fires when the Massive
 * dividend self-heal cron has detected NEW payments for stocks the
 * user holds. Same UX contract as the ex-div modal:
 *   • Fetches on portfolio page mount (3s debounce so we don't fight
 *     the initial dashboard hydration).
 *   • Renders ONE modal listing every fresh payment as a row with
 *     ticker · pay date · shares · per-share · total received.
 *   • "Dismiss all" hits POST /portfolio/dividend-payment-alerts/dismiss
 *     which suppresses the popup for the rest of the ET calendar day.
 *   • Per-row "x" hides just that row for the session (parent bell
 *     still shows the underlying notification until read/cleared).
 *
 * Copy explicitly instructs the user to check the stock row on the
 * Portfolio page to log the payment as a DRIP buy or cash — per the
 * user's Round 96d spec ("explain to check the stock row on the
 * portfolio page to make a drip buy or take it as cash").
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { X, DollarSign } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtMoney = (v) => {
  const n = Number(v);
  if (!isFinite(n)) return '—';
  return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const fmtPerShare = (v) => {
  const n = Number(v);
  if (!isFinite(n)) return '—';
  return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`;
};

const fmtShares = (v) => {
  const n = Number(v);
  if (!isFinite(n)) return '—';
  if (Number.isInteger(n)) return n.toLocaleString();
  return (Math.round(n * 10000) / 10000).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 4 });
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    const [y, m, d] = String(iso).split('-').map((x) => parseInt(x, 10));
    const dt = new Date(y, m - 1, d);
    return dt.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch {
    return iso;
  }
};

export default function DividendPaymentAlertModal({ onStockClick }) {
  const [open, setOpen] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [hidden, setHidden] = useState(() => new Set());

  const fetchAlerts = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/portfolio/dividend-payment-alerts`, { withCredentials: true });
      const list = Array.isArray(data?.alerts) ? data.alerts : [];
      const dismissedToday = !!data?.dismissed_today;
      setAlerts(list);
      if (!dismissedToday && list.length > 0) setOpen(true);
    } catch {
      // Silent — non-essential polling feature.
    }
  }, []);

  // 3s post-mount fetch, same as ex-div alerts so both pop-ups
  // never collide in the same render tick.
  useEffect(() => {
    const t = setTimeout(fetchAlerts, 3000);
    return () => clearTimeout(t);
  }, [fetchAlerts]);

  const visible = useMemo(
    () => alerts.filter((a) => !hidden.has(`${a.ticker}-${a.pay_date}`)),
    [alerts, hidden],
  );

  const handleDismissRow = (a) => {
    setHidden((prev) => {
      const next = new Set(prev);
      next.add(`${a.ticker}-${a.pay_date}`);
      return next;
    });
  };

  const handleDismissAll = async () => {
    try {
      await axios.post(`${API}/portfolio/dividend-payment-alerts/dismiss`, {}, { withCredentials: true });
    } catch {
      // Non-fatal.
    }
    setOpen(false);
  };

  const handleOpenStock = (ticker) => {
    if (onStockClick) onStockClick(ticker);
    setOpen(false);
  };

  if (!open || visible.length === 0) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      data-testid="dividend-payment-alert-modal"
      onClick={handleDismissAll}
    >
      <div
        className="w-full max-w-lg rounded-2xl border-2 border-emerald-500/40 bg-card p-5 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 mb-3">
          <div className="w-9 h-9 rounded-full bg-emerald-500/20 flex items-center justify-center">
            <DollarSign className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
          </div>
          <div className="flex-1">
            <h2 className="text-base font-bold text-foreground">Payment received</h2>
            <p className="text-xs text-muted-foreground">
              {visible.length === 1 ? '1 new dividend' : `${visible.length} new dividends`} landed since your last visit.
            </p>
          </div>
          <button
            type="button"
            onClick={handleDismissAll}
            className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            data-testid="dividend-payment-alert-close"
            title="Dismiss for the day"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
          {visible.map((a) => (
            <div
              key={`${a.ticker}-${a.pay_date}`}
              className="rounded-lg border border-emerald-400/40 bg-emerald-50/40 dark:bg-emerald-950/20 p-3"
              data-testid={`dividend-payment-alert-row-${a.ticker}`}
            >
              <div className="flex items-center justify-between gap-2">
                <button
                  type="button"
                  onClick={() => handleOpenStock(a.ticker)}
                  className="font-mono font-bold text-sm text-emerald-800 dark:text-emerald-200 hover:underline"
                  data-testid={`dividend-payment-alert-ticker-${a.ticker}`}
                >
                  {a.ticker}
                </button>
                <span className="text-lg font-bold text-emerald-700 dark:text-emerald-300">
                  {fmtMoney(a.amount)}
                </span>
                <button
                  type="button"
                  onClick={() => handleDismissRow(a)}
                  className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                  title={`Hide this ${a.ticker} alert for now`}
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
              <div className="mt-1 text-xs text-muted-foreground">
                Paid {fmtDate(a.pay_date)} · {fmtShares(a.shares)} sh × {fmtPerShare(a.per_share)}
              </div>
              <p className="mt-2 text-xs text-foreground/90 leading-relaxed">
                Check the <span className="font-semibold text-emerald-700 dark:text-emerald-300">{a.ticker}</span> row in your Portfolio to log this as a <span className="font-semibold">DRIP buy</span> (record the reinvested shares) or take it as <span className="font-semibold">cash</span> (added to inside cash).
              </p>
            </div>
          ))}
        </div>

        <div className="mt-4 flex items-center justify-between gap-2">
          <p className="text-[11px] text-muted-foreground">
            Alerts are also in your notification bell.
          </p>
          <button
            type="button"
            onClick={handleDismissAll}
            className="px-3 py-1.5 rounded-md text-xs font-semibold bg-muted text-foreground hover:bg-muted/80 transition-colors"
            data-testid="dividend-payment-alert-dismiss-all"
          >
            Dismiss all
          </button>
        </div>
      </div>
    </div>
  );
}
